import { Button } from '@talentmesh/ui/components/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { useClientsOverviewContext } from '../contexts/ClientsOverviewContext';
import UIStrings from '@/Utils/UIStrings';

const ClientsOverviewPagination = () => {
    const {
        pagination: { limit, offset, totalCount },
        setPagination,
    } = useClientsOverviewContext();

    const [currentPage, totalPages] = useMemo(() => {
        const total = Math.ceil(totalCount / limit) || 1;
        const current = Math.floor(offset / limit) + 1;
        return [current, total];
    }, [limit, offset, totalCount]);

    const paginationSizeInfo = useMemo<Parameters<typeof UIStrings.PaginationSizeInfo>>(() => {
        let currentSize = limit + offset;
        if (totalCount < currentSize) {
            currentSize = totalCount;
        }
        return [offset + 1, currentSize, totalCount];
    }, [limit, offset, totalCount]);

    const goToPage = (page: number) => {
        const newOffset = (page - 1) * limit;
        setPagination((p) => ({ ...p, offset: newOffset }));
    };

    return (
        <nav
            data-cy="clientsPagination"
            aria-label="Pagination"
            className="flex items-center justify-center gap-3 py-4"
        >
            <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                aria-label="Previous page"
            >
                <ChevronLeft className="size-4" />
            </Button>
            <span className="text-muted-foreground min-w-24 text-center text-sm">
                {UIStrings.PaginationSizeInfo(...paginationSizeInfo)}
            </span>
            <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
            >
                <ChevronRight className="size-4" />
            </Button>
        </nav>
    );
};

export default ClientsOverviewPagination;
